import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useLatestEvaluationResult } from '../hooks/useEvaluation';
import { evaluationService } from '../services/evaluationService';

function formatTimestamp(timestamp) {
  if (!timestamp) return 'N/A';
  try {
    return new Date(timestamp).toLocaleString();
  } catch (e) {
    return 'Invalid date';
  }
}

function OverallResults({ overallResults }) {
  if (!overallResults) return null;

  const airQualityColor = evaluationService.getAirQualityColor(overallResults.airQualityStatus);
  const moldRiskColor = evaluationService.getMoldRiskColor(overallResults.moldRisk);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon fontSize="small" />
        Overall Results
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Air Quality Status
          </Typography>
          <Chip
            label={overallResults.airQualityStatus || 'N/A'}
            color={airQualityColor}
            size="small"
            onClick={() => {}}
          />
        </Grid>

        {overallResults.airQualityTrend && (
          <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Air Quality Trend
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {evaluationService.getTrendIcon(overallResults.airQualityTrend)} {overallResults.airQualityTrend}
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Mold Risk
          </Typography>
          <Chip
            label={overallResults.moldRisk || 'N/A'}
            color={moldRiskColor}
            size="small"
            onClick={() => {}}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Dew Point
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {overallResults.dewPointCelsius !== null && overallResults.dewPointCelsius !== undefined
              ? `${overallResults.dewPointCelsius.toFixed(1)}°C`
              : 'N/A'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}

function PartialResults({ partialResults }) {
  if (!partialResults || partialResults.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Property Analysis
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Trend</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Units</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Sample Values</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partialResults.map((result, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={{ fontWeight: 500 }}>{result.observedProperty}</TableCell>
                <TableCell>
                  <Chip
                    label={result.status || 'N/A'}
                    size="small"
                    color={result.status === 'normal' ? 'success' : 'warning'}
                    variant="outlined"
                    onClick={() => {}}
                  />
                </TableCell>
                <TableCell>
                  {result.trend ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{evaluationService.getTrendIcon(result.trend)}</span>
                      <span>{result.trend}</span>
                    </Box>
                  ) : 'N/A'}
                </TableCell>
                <TableCell>{result.units || 'N/A'}</TableCell>
                <TableCell>
                  {result.filteredValues && result.filteredValues.length > 0
                    ? result.filteredValues.slice(0, 3).map(v => v.toFixed(1)).join(', ') +
                      (result.filteredValues.length > 3 ? '...' : '')
                    : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AnomalyDetectorResults({ anomalyDetector }) {
  if (!anomalyDetector) return null;

  const hasAnomaly = anomalyDetector.anomalyDetected;
  const needsCalibration = anomalyDetector.isCalibrationNeeded;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasAnomaly ? <WarningIcon color="warning" /> : <CheckCircleIcon color="success" />}
        Anomaly Detector
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Anomaly Detected
          </Typography>
          <Chip
            label={hasAnomaly ? 'Yes' : 'No'}
            color={hasAnomaly ? 'warning' : 'success'}
            size="small"
            onClick={() => {}}
          />
        </Grid>

        {hasAnomaly && (
          <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Impact
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {anomalyDetector.anomalyImpact}%
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Calibration Needed
          </Typography>
          <Chip
            label={needsCalibration ? 'Yes' : 'No'}
            color={needsCalibration ? 'error' : 'success'}
            size="small"
            onClick={() => {}}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Status
          </Typography>
          {anomalyDetector.error ? (
            <Chip label="Error" color="error" size="small" onClick={() => {}} />
          ) : (
            <Chip label="OK" color="success" size="small" onClick={() => {}} />
          )}
        </Grid>

        {anomalyDetector.calibrationRecommendations && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Recommendations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {anomalyDetector.calibrationRecommendations}
            </Typography>
          </Grid>
        )}

        {anomalyDetector.error && (
          <Grid item xs={12}>
            <Alert severity="error" sx={{ mt: 1 }}>
              {anomalyDetector.error}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function SubstanceClassifierResults({ substanceClassifier }) {
  if (!substanceClassifier) return null;

  const isClassified = substanceClassifier.anomalyClassified;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Substance Classifier
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Classification Status
          </Typography>
          <Chip
            label={isClassified ? 'Classified' : 'Not Classified'}
            color={isClassified ? 'primary' : 'default'}
            size="small"
            onClick={() => {}}
          />
        </Grid>

        {isClassified && substanceClassifier.substanceClass && substanceClassifier.substanceClass !== 'none' && (
          <>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Substance Class
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {substanceClassifier.substanceClass}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Confidence
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {substanceClassifier.substanceProbability}%
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
}

export default function EvaluationResultsPanel({ boxId }) {
  const { data: result, loading, error, refetch } = useLatestEvaluationResult(boxId);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>Loading evaluation results...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Error loading evaluation results: {error.message || 'Unknown error'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No evaluation results available for this box
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Latest Evaluation Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analysis Period: {formatTimestamp(result.analysisStartTs)} - {formatTimestamp(result.analysisEndTs)}
          </Typography>
        </Box>

        <OverallResults overallResults={result.overallResults} />

        <Divider sx={{ my: 3 }} />

        <PartialResults partialResults={result.partialResults} />

        <Divider sx={{ my: 3 }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Anomaly Detection
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AnomalyDetectorResults anomalyDetector={result.anomalyDetector} />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Substance Classification
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SubstanceClassifierResults substanceClassifier={result.substanceClassifier} />
          </AccordionDetails>
        </Accordion>

        {result.invalidSensors && result.invalidSensors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Invalid Sensors Detected:
              </Typography>
              {result.invalidSensors.join(', ')}
            </Alert>
          </Box>
        )}

        {result.invalidEntries && result.invalidEntries.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Invalid Entries: {result.invalidEntries.length}
              </Typography>
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}